import type { DateRange } from "@mui/x-date-pickers-pro";
import { endOfDay, isAfter, isBefore, isSameDay, isValid, parseISO } from "date-fns";
import type { DescriptiveDateRange } from "@entrilia/shared/utilities/dateFilterHelper";
import { dateRangeFactories } from "@entrilia/shared/utilities/dateFilterHelper";
import { formatDateShort } from "@entrilia/shared/utilities/dateUtils";
import type {
  DateFieldValue,
  DateFilterDefinition,
  DateFilterOperator,
  DateFilterValue,
  FilterHandler,
  OperatorOption,
} from "../filterTypes";

export interface DateFilterShortcut {
  label: string;
  getValue: () => DateRange<Date>;
}

const getShortcutValue = ({ from, to }: Pick<DescriptiveDateRange, "from" | "to">): DateRange<Date> => [
  from ?? null,
  to ?? endOfDay(Date.now()),
];

export const dateFilterShortcuts: readonly DateFilterShortcut[] = [
  { label: "Last 7 Days", getValue: () => getShortcutValue(dateRangeFactories.forLastDays(7)) },
  { label: "Last 30 Days", getValue: () => getShortcutValue(dateRangeFactories.forLastDays(30)) },
  { label: "This Month", getValue: () => getShortcutValue(dateRangeFactories.forThisMonth()) },
  { label: "This Quarter", getValue: () => getShortcutValue(dateRangeFactories.forThisQuarter()) },
  { label: "This Year", getValue: () => getShortcutValue(dateRangeFactories.forThisYear()) },
  { label: "Last Month", getValue: () => getShortcutValue(dateRangeFactories.forLastMonth()) },
  { label: "Last Quarter", getValue: () => getShortcutValue(dateRangeFactories.forLastQuarter()) },
  { label: "Last Year", getValue: () => getShortcutValue(dateRangeFactories.forLastYear()) },
];

export const dateFilterOperatorOptions: readonly OperatorOption<DateFilterOperator>[] = [
  { label: "is", value: "range" },
  { label: "is empty", value: "empty" },
  { label: "is not empty", value: "not_empty" },
] as const;

const tryGetValidDatesFromRange = (range: [string, string] | undefined) => {
  if (!range || !range[0] || !range[1]) {
    return {};
  }
  const dateFrom = parseISO(range[0]);
  const dateTo = parseISO(range[1]);
  if (!isValid(dateFrom) || !isValid(dateTo)) {
    return {};
  }
  return { dateFrom, dateTo };
};

export const dateFilterHandler: FilterHandler<DateFilterValue, DateFieldValue, DateFilterDefinition> = {
  getDefaultValue: (definition: DateFilterDefinition) => ({
    operator: definition.operatorOptions?.[0] ?? "range",
  }),

  isActive: ({ operator, range }: DateFilterValue) => {
    switch (operator) {
      case "range": {
        return !!range && !!range[0] && !!range[1];
      }
      case "empty": {
        return true;
      }
      case "not_empty": {
        return true;
      }
    }
  },

  formatValue: ({ operator, range }: DateFilterValue) => {
    switch (operator) {
      case "range": {
        const { dateFrom, dateTo } = tryGetValidDatesFromRange(range);
        if (!dateFrom || !dateTo) {
          return "";
        }
        return isSameDay(dateFrom, dateTo)
          ? `is ${formatDateShort(dateFrom)}`
          : `is ${formatDateShort(dateFrom)} - ${formatDateShort(dateTo)}`;
      }
      case "empty": {
        return "is empty";
      }
      case "not_empty": {
        return "is not empty";
      }
    }
  },

  matchesFieldValue: ({ operator, range }: DateFilterValue, fieldValue: DateFieldValue) => {
    switch (operator) {
      case "range": {
        const { dateFrom, dateTo } = tryGetValidDatesFromRange(range);
        if (!dateFrom || !dateTo) {
          return true;
        }
        if (!fieldValue) {
          return false;
        }
        if (Array.isArray(fieldValue)) {
          return fieldValue.some(
            (value) => !!value && isValid(value) && !isBefore(value, dateFrom) && !isAfter(value, dateTo)
          );
        }
        return isValid(fieldValue) && !isBefore(fieldValue, dateFrom) && !isAfter(fieldValue, dateTo);
      }

      case "empty": {
        return !fieldValue || !isValid(fieldValue);
      }

      case "not_empty": {
        return !!fieldValue && isValid(fieldValue);
      }
    }
  },
};
